import React, { useEffect } from 'react'
import {
  Box,
  Group,
  Avatar,
  Menu,
  Badge,
  useMantineColorScheme
} from '@mantine/core'
import { IoAdd, IoSettingsOutline, IoLogOut, IoSearchOutline, IoMoonOutline, IoSunnyOutline, IoInformationCircleOutline } from 'react-icons/io5'
import { Link, useLocation } from 'react-router-dom'
import { useStore } from '../stores/store'
import { observer } from 'mobx-react-lite'
import { AppIconButton, AppText } from '../common/ui'
import navLogoUrl from '../icon-ak-sil/icon-styled.svg?url'

export const TOP_NAV_HEIGHT = 56

interface TopNavBarProps {
  onUploadClick?: () => void;
  onSettingsClick?: () => void;
}

export default observer(function TopNavBar ({ onUploadClick, onSettingsClick }: TopNavBarProps) {
  const { userStore, moderationStore } = useStore()
  const location = useLocation()
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const user = userStore.user

  // Must run before any conditional return — same hook order on /login and after redirect.
  useEffect(() => {
    if (!user?.isModerator && !user?.isAdmin) return
    void moderationStore.loadOpenFlagCount()
    const id = window.setInterval(() => void moderationStore.loadOpenFlagCount(), 120_000)
    return () => window.clearInterval(id)
  }, [user?.isModerator, user?.isAdmin, moderationStore])

  const hiddenRoutes = ['/login', '/register', '/forgot-password', '/reset-password']
  if (hiddenRoutes.some((route) => location.pathname.startsWith(route))) {
    return null
  }

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: TOP_NAV_HEIGHT,
        backgroundColor: 'var(--gs-bg-1)',
        borderBottom: '1px solid var(--gs-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 12,
        paddingRight: 12,
        zIndex: 101
      }}
    >
      {/* Left: + (fixed slot keeps logo centered) */}
      <Box sx={{ width: 44, display: 'flex', justifyContent: 'flex-start' }}>
        {userStore.isLoggedIn
          ? (
          <AppIconButton
            variant="filled"
            size={44}
            radius="xl"
            onClick={onUploadClick}
            sx={{
              backgroundColor: 'var(--gs-accent)',
              color: 'var(--gs-text-primary)',
              fontWeight: 800,
              '&:hover': { backgroundColor: 'var(--gs-accent-dark)' }
            }}
            aria-label="Upload"
          >
            <IoAdd size={26} strokeWidth={48} />
          </AppIconButton>
            )
          : (
          <Box sx={{ width: 44 }} />
            )}
      </Box>

      {/* Center: logo + wordmark */}
      <Box
        component={Link}
        to="/slop-feed"
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          textDecoration: 'none',
          color: 'inherit'
        }}
      >
        <Box
          component="img"
          src={navLogoUrl}
          alt="GunSlop"
          sx={{
            width: 55,
            height: 55,
            borderRadius: 8,
            objectFit: 'contain',
            flexShrink: 0
          }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.style.display = 'none'
          }}
        />
        <AppText
          component="span"
          sx={{
            fontFamily: 'Oswald, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: 2,
            color: 'var(--gs-text-primary)',
            margin: 0,
            lineHeight: 1
          }}
        >
          GUNSLOP
        </AppText>
      </Box>

      {/* Right: settings menu */}
      <Box sx={{ width: 44, display: 'flex', justifyContent: 'flex-end' }}>
        <Menu shadow="md" width={240} position="bottom-end">
          <Menu.Target>
            <AppIconButton variant="subtle" color="gray" size={44} radius="md" aria-label="Menu">
              <IoSettingsOutline size={26} />
            </AppIconButton>
          </Menu.Target>
          <Menu.Dropdown>
            {user && (
              <>
                <Menu.Item disabled>
                  <Group spacing="xs">
                    <Avatar size="sm" src={user.image} alt={user.displayName} radius="xl" />
                    <div>
                      <AppText size="sm" weight={600}>
                        {user.displayName}
                      </AppText>
                      <AppText size="xs" color="dimmed">
                        @{user.username}
                      </AppText>
                    </div>
                  </Group>
                </Menu.Item>
                <Menu.Divider />
              </>
            )}
            <Menu.Item
              component={Link}
              to="/search"
              icon={<IoSearchOutline size={16} />}
            >
              Search
            </Menu.Item>
            <Menu.Item
              component={Link}
              to="/about"
              icon={<IoInformationCircleOutline size={16} />}
            >
              About us
            </Menu.Item>
            {user && (
              <Menu.Item component={Link} to="/profile">
                Profile
              </Menu.Item>
            )}
            <Menu.Item
              icon={colorScheme === 'dark' ? <IoSunnyOutline size={16} /> : <IoMoonOutline size={16} />}
              onClick={() => toggleColorScheme()}
            >
              {colorScheme === 'dark' ? 'Light mode' : 'Dark mode'}
            </Menu.Item>
            {user && (user.isModerator || user.isAdmin) && (
              <Menu.Item
                component={Link}
                to="/moderation"
                rightSection={
                  moderationStore.openFlagCount > 0
                    ? (
                    <Badge size="xs" color="red" variant="filled">
                      {moderationStore.openFlagCount > 99 ? '99+' : moderationStore.openFlagCount}
                    </Badge>
                      )
                    : null
                }
              >
                Moderation
              </Menu.Item>
            )}
            {user?.isAdmin && (
              <>
                <Menu.Divider />
                <Menu.Item component={Link} to="/admin/users">
                  Admin: Users
                </Menu.Item>
                <Menu.Item component={Link} to="/admin/overseer">
                  Admin: Overseer
                </Menu.Item>
                <Menu.Item component={Link} to="/admin/content">
                  Admin: Content
                </Menu.Item>
              </>
            )}
            <Menu.Item
              onClick={() => onSettingsClick?.()}
              icon={<IoSettingsOutline size={16} />}
            >
              Settings
            </Menu.Item>
            {user
              ? (
              <>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  icon={<IoLogOut size={16} />}
                  onClick={() => userStore.logout()}
                >
                  Logout
                </Menu.Item>
              </>
                )
              : (
              <>
                <Menu.Divider />
                <Menu.Item component={Link} to="/login">
                  Login
                </Menu.Item>
                <Menu.Item component={Link} to="/register">
                  Register
                </Menu.Item>
              </>
                )}
          </Menu.Dropdown>
        </Menu>
      </Box>
    </Box>
  )
})
